/**
 * Cockpit visual editor — the COMPONENT PALETTE.
 *
 * Lists the catalog's 30 insertable components (the root CockpitContainer is
 * excluded — it's only ever a page root) grouped by category. Clicking a tile
 * inserts a node seeded from that component's golden fixture / schema defaults
 * under the currently-selected parent (default: the page container). A search
 * box filters across labels + descriptions.
 */

import { useMemo, useState } from 'react';
import { Search, Database, Zap } from 'lucide-react';
import { Input } from '@/components/ui/input';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { getPaletteGroups, type PaletteEntry } from './cockpit-editor-model';

export interface CockpitPaletteProps {
  /** Insert a component slug under the selected parent. */
  onInsert: (slug: string) => void;
  /** When false (read-only), tiles are disabled. */
  disabled?: boolean;
  /** Label of the node the new component will be nested under (for the hint). */
  parentLabel?: string;
}

export function CockpitPalette({ onInsert, disabled, parentLabel }: CockpitPaletteProps) {
  const [query, setQuery] = useState('');
  const groups = useMemo(() => getPaletteGroups(), []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return groups;
    return groups
      .map((g) => ({
        ...g,
        entries: g.entries.filter(
          (e) =>
            e.label.toLowerCase().includes(q) ||
            e.slug.toLowerCase().includes(q) ||
            e.description.toLowerCase().includes(q),
        ),
      }))
      .filter((g) => g.entries.length > 0);
  }, [groups, query]);

  return (
    <div className="flex h-full flex-col">
      <div className="shrink-0 border-b border-border/40 p-2">
        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search components…"
            aria-label="Search components"
            className="h-8 pl-8 text-xs"
          />
        </div>
        {parentLabel ? (
          <p className="mt-1.5 text-[10px] text-muted-foreground">
            Inserts under <span className="font-medium text-foreground">{parentLabel}</span>
          </p>
        ) : null}
      </div>
      <div className="flex-1 overflow-y-auto p-2">
        {filtered.length === 0 ? (
          <p className="px-1 py-4 text-center text-xs text-muted-foreground">
            No components match “{query}”.
          </p>
        ) : (
          filtered.map((group) => (
            <div key={group.category} className="mb-3">
              <h3 className="mb-1.5 px-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                {group.label}
              </h3>
              <div className="grid grid-cols-1 gap-1">
                {group.entries.map((entry) => (
                  <PaletteTile
                    key={entry.slug}
                    entry={entry}
                    disabled={disabled}
                    onInsert={() => onInsert(entry.slug)}
                  />
                ))}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function PaletteTile({
  entry,
  disabled,
  onInsert,
}: {
  entry: PaletteEntry;
  disabled?: boolean;
  onInsert: () => void;
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          onClick={onInsert}
          data-testid={`cockpit-palette-${entry.slug}`}
          className="group flex w-full items-center justify-between gap-2 rounded-md border border-border/50 bg-card px-2.5 py-1.5 text-left transition-colors hover:border-primary/50 hover:bg-accent disabled:cursor-not-allowed disabled:opacity-50"
        >
          <span className="min-w-0 truncate text-xs font-medium">{entry.label}</span>
          <span className="flex shrink-0 items-center gap-1 text-muted-foreground">
            {entry.hasBindings ? (
              <Database className="h-3 w-3" aria-label="Has data binding" />
            ) : null}
            {entry.hasActions ? (
              <Zap className="h-3 w-3" aria-label="Has action" />
            ) : null}
          </span>
        </button>
      </TooltipTrigger>
      <TooltipContent side="right" className="max-w-xs">
        <p className="font-medium">{entry.label}</p>
        <p className="mt-0.5 text-xs text-muted-foreground">{entry.description}</p>
      </TooltipContent>
    </Tooltip>
  );
}
